/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { isAxiosError, type AxiosError } from 'axios';
import { getCookies } from '@/utils/cookie';
import { COOKIES } from '@/constants/cookies';
import { type NextApiRequest, type NextApiResponse } from 'next';
import { serializeError } from 'serialize-error';

type ExtendedRequest = Omit<NextApiRequest, 'body'> & { body: unknown };

/**
 * Normalizes axios error data for logging, extracting relevant request/response details.
 */
export async function normalizeErrorForLogging(error: unknown) {
  const axiosError: AxiosError | null = isAxiosError(error) ? error : null;
  const serializedError = serializeError(error);

  const request = axiosError?.request as ExtendedRequest | undefined;
  const response = axiosError?.response as NextApiResponse | undefined;

  const cookies =
    request?.headers && response
      ? await getCookies({
          req: request,
          res: response,
        })
      : null;

  return {
    name: serializedError.name,
    message: serializedError.message,
    stack: serializedError.stack,
    url: axiosError?.config?.url,
    status: response?.status,
    headers: axiosError?.response?.headers,
    requestBody: request?.body,
    JSESSIONID: cookies?.[COOKIES.JSESSIONID],
  };
}
